import {
  type PublicProfile,
  type UsernameFormatCode,
  usernameFormatCode,
} from "@pax/api-contract/common";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@pax/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type SubmitEvent, useEffect, useState } from "react";
import { ProfileAppeals } from "@/features/moderation/ItemAppeals";
import { SubmitError } from "@/features/moderation/SubmitError";
import { api, reachable } from "@/lib/api";
import { profileByUsernameQuery, profileQuery, usernameAvailabilityQuery } from "../queries";

const PROFILE_USERNAME_INPUT = ["profile_username"] as const;

/** How long typing pauses before the username is checked. */
const CHECK_DELAY_MS = 300;

const FORMAT_MESSAGES: Record<UsernameFormatCode, string> = {
  required: "Enter a username",
  too_short: "At least 3 characters",
  too_long: "At most 25 characters",
  invalid_chars: "Letters, numbers, and underscores only",
  must_start_with_letter: "Start with a letter",
  edge_underscore: "No underscore at the start or end",
  consecutive_underscore: "No two underscores in a row",
};

const renameDate = new Intl.DateTimeFormat("en", { month: "long", day: "numeric" });

/** Your username on its own, checked as you type. */
export function UsernameDialog({
  profile,
  open,
  onOpenChange,
  onChanged,
}: {
  profile: PublicProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new username once it's saved. */
  onChanged?: (username: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Username</DialogTitle>
        </DialogHeader>
        {/* Mounted with the dialog, so every opening starts from what the profile says now. */}
        {open ? (
          <UsernameForm
            profile={profile}
            onDone={() => onOpenChange(false)}
            onChanged={onChanged}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function UsernameForm({
  profile,
  onDone,
  onChanged,
}: {
  profile: PublicProfile;
  onDone: () => void;
  onChanged?: (username: string) => void;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(profile.username ?? "");
  const username = value.trim();
  const checked = useCheckedUsername(username);
  const formatCode = usernameFormatCode(username);
  const availability = useQuery({
    ...usernameAvailabilityQuery(checked),
    enabled: usernameFormatCode(checked) === null,
    placeholderData: keepPreviousData,
  });
  const result = checked === username && !formatCode ? availability.data : undefined;
  const unchanged = username === profile.username;

  const save = useMutation({
    mutationFn: () =>
      reachable(
        api.profiles.username.set({
          params: { profileId: profile.profileId },
          body: { username },
        }),
      ),
    onSuccess: response => {
      const saved = response.profile;
      const merge = <Response extends { profile: PublicProfile }>(current: Response | undefined) =>
        current ? { ...current, profile: saved } : { profile: saved };
      queryClient.setQueryData(profileQuery(saved.profileId).queryKey, merge);
      if (saved.username) {
        queryClient.setQueryData(profileByUsernameQuery(saved.username).queryKey, merge);
        onChanged?.(saved.username);
      }
      onDone();
    },
  });

  const note = formatCode
    ? username === ""
      ? null
      : { text: FORMAT_MESSAGES[formatCode], ok: false }
    : !result
      ? null
      : result.name.status === "taken"
        ? { text: "Username taken", ok: false }
        : result.name.status === "invalid"
          ? { text: FORMAT_MESSAGES[result.name.formatCode], ok: false }
          : result.name.status === "current"
            ? { text: `${window.location.host}/@${username}`, ok: true }
            : { text: `Available · ${window.location.host}/@${username}`, ok: true };

  function submit(event: SubmitEvent) {
    event.preventDefault();
    if (result?.canSubmit && !unchanged) save.mutate();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="relative">
          <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground">
            @
          </span>
          <Input
            aria-label="Username"
            value={value}
            onChange={event => setValue(event.target.value)}
            maxLength={25}
            spellCheck={false}
            autoCapitalize="none"
            autoComplete="off"
            autoFocus
            className="pl-7"
          />
        </div>
        <p
          aria-live="polite"
          className={cn(
            "min-h-[1lh] text-xs",
            note?.ok === false ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {note?.text}
        </p>
      </div>

      <p className="text-xs text-pretty text-muted-foreground">
        You can change your username twice every 90 days. Links to your old username stop working.
        {result?.nextRenameAvailableAt ? (
          <>
            {" "}
            Your next change is available on{" "}
            {renameDate.format(new Date(result.nextRenameAvailableAt))}.
          </>
        ) : null}
      </p>

      <ProfileAppeals profileId={profile.profileId} inputKinds={PROFILE_USERNAME_INPUT} />
      <SubmitError error={save.error} inputs="username" />

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending || unchanged || !result?.canSubmit}>
          Save
        </Button>
      </DialogFooter>
    </form>
  );
}

/** The username as typed, once typing has paused. */
function useCheckedUsername(username: string): string {
  const [checked, setChecked] = useState(username);
  useEffect(() => {
    const timer = setTimeout(() => setChecked(username), CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [username]);
  return checked;
}
